import { LinearProgress } from '@mui/joy';
import { Row, Table } from '@tanstack/react-table';

import {
  ComponentProps,
  ReactNode,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { TableComponents, TableVirtuoso } from 'react-virtuoso';

import { TableSkeleton } from '@/Pages/Home/ResourceTable/TableSkeleton';
import { cn } from '@/utils/tailwind';

import {
  DataGridEmptyState,
  DataGridProps,
  DataGridTableHeaderContent,
  DataGridTableHeaderProps,
  DataGridTableRow,
} from './DataGrid';
import { useDelayedLoading } from './DataGrid.utils';
import { TableBody, TableHeader } from './Table';

const ViewportHeightContext = createContext<number | undefined>(undefined);

export interface DataGridVirtualProps<TData> extends DataGridProps<TData> {
  footer?: ReactNode;
}

const DataGridVirtualSizeMonitor = (props: {
  children: ReactNode;
  footer: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState<number | undefined>(undefined);

  useEffect(() => {
    const paddingRef = ref.current;
    if (!paddingRef) return () => void 0;

    // there are two hacks present in this component
    // 1. ensure the footer is sticky + left: 0 relative to the scroll container
    // 2. ensure the column resize handle covers _exactly_ the entire height of table
    //    (not relying on min-h-1000vh)

    // ad 1) sticky + left: 0 doesn't seem to work unless it's part of the table
    //       thus the idea here is simulate the sticky position by translating the
    //       footer

    // ad 2) we measure the offset height on every scroll, which seems fine to me,
    //       as the mismatch will go away the moment user scrolls a bit

    // this is a very ugly hack, but inspecting the
    // react-virtuoso library there doesn't seem to be a nice way of
    // obtaining the actual dom reference of the scroller dom node
    // nor the dom node of the viewport...
    const viewport = paddingRef.parentElement;
    const scroller = viewport?.parentElement;

    const onScroll = () => {
      const offset = scroller?.scrollLeft;
      if (offset != null) {
        paddingRef.style.transform = `translateX(${offset}px)`;
      }

      if (viewport != null) {
        setValue(viewport.offsetHeight);
      }
    };

    let timer = 0;
    const debouncedScroll = () => {
      clearTimeout(timer);
      timer = window.setTimeout(onScroll, 100);
    };

    onScroll();
    scroller?.addEventListener('scroll', debouncedScroll);
    return () => scroller?.removeEventListener('scroll', debouncedScroll);
  }, []);

  return (
    <>
      <ViewportHeightContext.Provider value={value}>
        {props.children}
      </ViewportHeightContext.Provider>
      <div className="transition-transform" ref={ref}>
        {props.footer}
      </div>
    </>
  );
};

type TableComponentProps<Key extends keyof TableComponents> = ComponentProps<
  Exclude<TableComponents<unknown, unknown>[Key], undefined>
>;

const DataGridVirtualTable = forwardRef<
  HTMLTableElement,
  TableComponentProps<'Table'>
>((props, ref) => {
  const context = props.context as DataGridVirtualProps<unknown>;
  return (
    <DataGridVirtualSizeMonitor footer={context.footer}>
      <table
        ref={ref}
        className="object -ml-px min-w-full caption-bottom text-sm"
        style={{
          ...props.style,
          width: context.table.getCenterTotalSize(),
        }}
      >
        {props.children}
      </table>
    </DataGridVirtualSizeMonitor>
  );
});

const DataGridVirtualTableHead = forwardRef<
  HTMLTableSectionElement,
  TableComponentProps<'TableHead'>
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
>(({ context: _, ...props }, ref) => (
  <TableHeader
    key={props.key}
    style={props.style}
    ref={props.ref ?? ref}
    className="sticky top-0 z-10 bg-secondary"
  >
    {props.children}
  </TableHeader>
));

function VirtualDataGridTableRow<TData>(
  props: TableComponentProps<'TableRow'>
) {
  const {
    style,
    item: rawRow,
    context: rawContext,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    children: _,
    ...dataProps
  } = props;

  const context = rawContext as DataGridVirtualProps<unknown>;
  const table = context.table as Table<TData>;
  const row = rawRow as Row<TData>;

  const isEmpty = context.table.getRowModel().rows.length <= 0;

  return (
    <DataGridTableRow
      key={row.id}
      {...dataProps}
      {...context}
      style={style}
      table={table}
      row={row}
      isEmpty={isEmpty}
    />
  );
}

function VirtualDataGridHeader<TData>(props: DataGridTableHeaderProps<TData>) {
  const resizeHandleHeight = useContext(ViewportHeightContext);
  return (
    <DataGridTableHeaderContent
      {...props}
      resizeHandleHeight={props.resizeHandleHeight ?? resizeHandleHeight}
    />
  );
}

export function DataGridVirtual<TData>(props: DataGridVirtualProps<TData>) {
  const isEmpty = props.table.getRowModel().rows.length <= 0;
  const isLoading = useDelayedLoading(props.isLoading);
  const isImmediateLoading = props.isLoading && isEmpty;
  const { rows } = props.table.getRowModel();

  const allColumnsHidden =
    !props.table
      .getAllLeafColumns()
      .some((column) => column.getIsVisible() && column.getCanHide()) &&
    props.table.getAllLeafColumns().some((column) => column.getCanHide());

  let content: ReactNode | null = null;
  if (isEmpty && !props.isLoading) {
    content = props.emptyState;
  } else if (isImmediateLoading) {
    content = (
      <TableSkeleton
        rows={props.loadingRowCount ?? 10}
        rowHeight={props.cellHeight}
        hideHeader={props.hideHeader}
        className="rounded-none border-none"
      />
    );
  } else {
    content = (
      <>
        {isLoading && (
          <div className={cn('absolute top-[48px] w-full -translate-y-[3px]')}>
            <LinearProgress />
          </div>
        )}
        <div className="absolute inset-0">
          {allColumnsHidden ? (
            <DataGridEmptyState cellHeight={props.cellHeight} />
          ) : (
            <TableVirtuoso
              data={rows}
              context={props}
              components={{
                TableBody: TableBody,
                Table: DataGridVirtualTable,
                TableHead: DataGridVirtualTableHead,
                TableRow: VirtualDataGridTableRow,
              }}
              increaseViewportBy={300}
              fixedHeaderContent={() => (
                <VirtualDataGridHeader
                  table={props.table}
                  cellSpacingVariant={props.cellSpacingVariant}
                  showDividerByDefault={props.showDividerByDefault}
                />
              )}
            />
          )}
        </div>
      </>
    );
  }
  return <div className="relative h-full">{content}</div>;
}
